import type { FuelStation, Location, StationRecommendation } from '@/types';
import { clusteringConfigService } from '@/services/clusteringConfig';

/**
 * Visual density-based clustering using pixel coordinates and marker overlap detection
 * This approach clusters stations based on visual overlap on the map rather than geographic distance
 */

export interface PixelPosition {
  x: number;
  y: number;
}

export interface MarkerBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StationWithPixelPosition extends FuelStation {
  pixelPosition: PixelPosition;
  markerBounds: MarkerBounds;
  // Optional pricing data for cluster price calculations
  currentPrice?: number; // in pence
  fuelType?: string;
}

export interface VisualClusterData {
  id: string;
  stations: FuelStation[];
  center: Location;
  bounds: MarkerBounds;
  station_count: number;
  brand_count: number;
  brands: string[];
  dominant_brands: string[];
  // Pricing information
  average_price_pence?: number;
  price_range?: {
    min_pence: number;
    max_pence: number;
  };
  stations_with_prices?: number;
}

export interface VisualClusteringResult {
  clusters: VisualClusterData[];
  individualStations: FuelStation[];
  debugInfo: {
    totalStations: number;
    clusteredStations: number;
    individualStations: number;
    processingTime: number;
  };
}

/**
 * Convert latitude/longitude to pixel coordinates using Web Mercator projection
 * This matches Leaflet's coordinate system
 */
export function latLngToPixel(
  lat: number,
  lng: number,
  zoom: number,
  mapCenter: Location,
  mapSize: { width: number; height: number }
): PixelPosition {
  const TILE_SIZE = 256;
  const worldSize = TILE_SIZE * Math.pow(2, zoom);
  
  // Convert to radians
  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const centerLatRad = (mapCenter.latitude * Math.PI) / 180;
  const centerLngRad = (mapCenter.longitude * Math.PI) / 180;
  
  // Web Mercator projection
  const worldX = ((lngRad + Math.PI) / (2 * Math.PI)) * worldSize;
  const worldY = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * worldSize;
  
  const centerWorldX = ((centerLngRad + Math.PI) / (2 * Math.PI)) * worldSize;
  const centerWorldY = ((1 - Math.log(Math.tan(centerLatRad) + 1 / Math.cos(centerLatRad)) / Math.PI) / 2) * worldSize;
  
  // Convert to pixel coordinates relative to map center
  const pixelX = mapSize.width / 2 + (worldX - centerWorldX);
  const pixelY = mapSize.height / 2 + (worldY - centerWorldY);
  
  return { x: pixelX, y: pixelY };
}

/**
 * Get marker size based on station type and zoom level
 */
export function getMarkerSize(zoomLevel: number, isCluster: boolean = false, stationCount?: number): number {
  if (isCluster && stationCount) {
    // Logarithmic scaling for cluster markers (synchronized with visual rendering)
    const baseSize = 28;
    const maxSize = 50; // Matches VisualClusterMarker sizing
    return Math.min(maxSize, baseSize + Math.log(stationCount) * 6); // Controlled growth rate
  }
  
  // Individual station marker sizes based on zoom level
  if (zoomLevel <= 8) return 20;
  if (zoomLevel <= 10) return 24;
  if (zoomLevel <= 12) return 28;
  return 32;
}

/**
 * Calculate marker bounding box with padding to prevent visual crowding
 */
export function calculateMarkerBounds(
  position: PixelPosition,
  markerSize: number,
  padding: number = 8
): MarkerBounds {
  const totalSize = markerSize + padding * 2;
  return {
    x: position.x - totalSize / 2,
    y: position.y - totalSize / 2,
    width: totalSize,
    height: totalSize,
  };
}

/**
 * Check if two marker bounding boxes overlap
 */
export function boundingBoxesOverlap(bounds1: MarkerBounds, bounds2: MarkerBounds): boolean {
  return (
    bounds1.x < bounds2.x + bounds2.width &&
    bounds1.x + bounds1.width > bounds2.x &&
    bounds1.y < bounds2.y + bounds2.height &&
    bounds1.y + bounds1.height > bounds2.y
  );
}

/**
 * Find all stations that overlap with a given station
 */
export function findOverlappingStations(
  targetStation: StationWithPixelPosition,
  allStations: StationWithPixelPosition[]
): StationWithPixelPosition[] {
  const overlapping: StationWithPixelPosition[] = [targetStation];
  
  for (const station of allStations) {
    if (station.id === targetStation.id) continue;
    
    if (boundingBoxesOverlap(targetStation.markerBounds, station.markerBounds)) {
      overlapping.push(station);
    }
  }
  
  return overlapping;
}

/**
 * Calculate cluster marker size and position for visual overlap detection
 */
export function calculateClusterMarkerInfo(
  stations: StationWithPixelPosition[],
  zoomLevel: number,
  padding: number = 8
): { position: PixelPosition; bounds: MarkerBounds; markerSize: number } {
  // Calculate cluster center (average position)
  const avgX = stations.reduce((sum, s) => sum + s.pixelPosition.x, 0) / stations.length;
  const avgY = stations.reduce((sum, s) => sum + s.pixelPosition.y, 0) / stations.length;
  
  const position: PixelPosition = { x: avgX, y: avgY };
  
  // Calculate cluster marker size (logarithmic scaling)
  const markerSize = getMarkerSize(zoomLevel, true, stations.length);
  
  // Calculate bounding box for overlap detection
  const bounds = calculateMarkerBounds(position, markerSize, padding);
  
  return { position, bounds, markerSize };
}

/**
 * Check if a cluster would overlap with existing clusters or individual stations
 */
export function wouldClusterOverlap(
  clusterStations: StationWithPixelPosition[],
  existingClusters: { bounds: MarkerBounds }[],
  existingStations: StationWithPixelPosition[],
  zoomLevel: number,
  padding: number = 8
): boolean {
  const clusterInfo = calculateClusterMarkerInfo(clusterStations, zoomLevel, padding);
  
  // Check overlap with existing clusters
  for (const existingCluster of existingClusters) {
    if (boundingBoxesOverlap(clusterInfo.bounds, existingCluster.bounds)) {
      return true;
    }
  }
  
  // Check overlap with individual stations
  for (const station of existingStations) {
    if (boundingBoxesOverlap(clusterInfo.bounds, station.markerBounds)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Merge overlapping clusters together
 */
export function mergeOverlappingClusters(
  clusters: VisualClusterData[],
  stationsWithPixels: StationWithPixelPosition[],
  zoomLevel: number,
  padding: number = 8
): VisualClusterData[] {
  if (clusters.length <= 1) return clusters;
  
  // Create station lookup for quick access
  const stationMap = new Map<number, StationWithPixelPosition>();
  stationsWithPixels.forEach(s => stationMap.set(s.id, s));
  
  // Calculate cluster marker info for overlap detection
  const clusterInfos = clusters.map(cluster => {
    const clusterStations = cluster.stations.map(s => stationMap.get(s.id)).filter(Boolean) as StationWithPixelPosition[];
    return {
      cluster,
      ...calculateClusterMarkerInfo(clusterStations, zoomLevel, padding)
    };
  });
  
  // Find overlapping clusters and group them
  const processed = new Set<string>();
  const mergedClusters: VisualClusterData[] = [];
  
  for (let i = 0; i < clusterInfos.length; i++) {
    const currentInfo = clusterInfos[i];
    if (processed.has(currentInfo.cluster.id)) continue;
    
    // Find all clusters that overlap with the current one
    const overlappingClusters: typeof currentInfo[] = [currentInfo];
    processed.add(currentInfo.cluster.id);
    
    for (let j = i + 1; j < clusterInfos.length; j++) {
      const otherInfo = clusterInfos[j];
      if (processed.has(otherInfo.cluster.id)) continue;
      
      // Check if any cluster in the current group overlaps with this cluster
      const overlaps = overlappingClusters.some(groupCluster => 
        boundingBoxesOverlap(groupCluster.bounds, otherInfo.bounds)
      );
      
      if (overlaps) {
        overlappingClusters.push(otherInfo);
        processed.add(otherInfo.cluster.id);
      }
    }
    
    // Merge overlapping clusters or keep single cluster
    if (overlappingClusters.length > 1) {
      // Merge all overlapping clusters into one
      const allStations: FuelStation[] = [];
      overlappingClusters.forEach(info => {
        allStations.push(...info.cluster.stations);
      });
      
      // Remove duplicates (shouldn't happen but safety first)
      const uniqueStations = Array.from(
        new Map(allStations.map(s => [s.id, s])).values()
      );
      
      // Create merged cluster with pixel positions for proper center calculation
      const stationsWithPos = uniqueStations.map(s => stationMap.get(s.id)).filter(Boolean) as StationWithPixelPosition[];
      const mergedCluster = createVisualCluster(stationsWithPos, `merged-cluster-${mergedClusters.length}`);
      mergedClusters.push(mergedCluster);
      
      console.log(`🔗 Merged ${overlappingClusters.length} overlapping clusters into one (${uniqueStations.length} total stations)`);
    } else {
      // Keep single cluster as-is
      mergedClusters.push(currentInfo.cluster);
    }
  }
  
  return mergedClusters;
}

/**
 * Create a visual cluster from a group of overlapping stations
 */
export function createVisualCluster(
  stations: StationWithPixelPosition[],
  clusterId: string
): VisualClusterData {
  // Calculate cluster center (average position)
  const avgLat = stations.reduce((sum, s) => sum + s.location.latitude, 0) / stations.length;
  const avgLng = stations.reduce((sum, s) => sum + s.location.longitude, 0) / stations.length;
  
  // Get unique brands
  const brands = [...new Set(stations.map(s => s.brand))].sort();
  const brandCounts = brands.map(brand => ({
    brand,
    count: stations.filter(s => s.brand === brand).length
  }));
  
  // Sort by count to get dominant brands
  const dominantBrands = brandCounts
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map(b => b.brand);
  
  // Calculate pricing information for the cluster
  const stationsWithPrices = stations.filter(s => s.currentPrice !== undefined);
  let average_price_pence: number | undefined;
  let price_range: { min_pence: number; max_pence: number } | undefined;
  
  if (stationsWithPrices.length > 0) {
    const prices = stationsWithPrices.map(s => s.currentPrice!);
    average_price_pence = Math.round(prices.reduce((sum, price) => sum + price, 0) / prices.length);
    price_range = {
      min_pence: Math.min(...prices),
      max_pence: Math.max(...prices),
    };
  }

  // Calculate cluster bounding box (union of all station bounds)
  const minX = Math.min(...stations.map(s => s.markerBounds.x));
  const minY = Math.min(...stations.map(s => s.markerBounds.y));
  const maxX = Math.max(...stations.map(s => s.markerBounds.x + s.markerBounds.width));
  const maxY = Math.max(...stations.map(s => s.markerBounds.y + s.markerBounds.height));
  
  return {
    id: clusterId,
    stations: stations.map(s => ({
      id: s.id,
      site_id: s.site_id || `site-${s.id}`,
      retailer_id: s.retailer_id || 0,
      brand: s.brand,
      address: s.address,
      postcode: s.postcode || '',
      location: s.location,
      active: s.active,
      first_seen: s.first_seen || new Date().toISOString(),
      last_updated: s.last_updated || new Date().toISOString(),
      retailer_name: s.retailer_name,
      distance_miles: s.distance_miles
    })),
    center: {
      latitude: avgLat,
      longitude: avgLng,
    },
    bounds: {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    },
    station_count: stations.length,
    brand_count: brands.length,
    brands,
    dominant_brands: dominantBrands,
    // Pricing data
    average_price_pence,
    price_range,
    stations_with_prices: stationsWithPrices.length,
  };
}

/**
 * Enhanced DBSCAN-based clustering algorithm with pricing data
 * Groups stations based on geographic proximity rather than visual overlap
 */
export async function performVisualClusteringWithPricing(
  stationRecommendations: StationRecommendation[],
  fuelCode: string,
  zoomLevel: number,
  mapCenter: Location,
  mapSize: { width: number; height: number }, // Real map size for accurate pixel calculations
  options: {
    minClusterSize?: number;
    padding?: number;
    maxStations?: number;
    mapBounds?: { south: number; north: number; west: number; east: number }; // Legacy parameter, not used in pixel-based DBSCAN
  } = {}
): Promise<VisualClusteringResult> {
  const startTime = performance.now();
  
  // Get configurable clustering threshold
  const clusteringThreshold = await clusteringConfigService.getClusteringThreshold();
  
  // Early exit: No clustering above threshold, show all stations as individuals
  if (zoomLevel > clusteringThreshold) {
    const individualStations: FuelStation[] = stationRecommendations.map(sr => sr.station);
    const processingTime = performance.now() - startTime;
    
    console.log(`🎯 High zoom (${zoomLevel} > ${clusteringThreshold}) - No clustering: ${individualStations.length} individual stations`);
    
    return {
      clusters: [], // No clusters at high zoom
      individualStations,
      debugInfo: {
        totalStations: stationRecommendations.length,
        clusteredStations: 0,
        individualStations: individualStations.length,
        processingTime,
      },
    };
  }
  
  const {
    maxStations = 500
    // mapBounds no longer needed for pixel-based DBSCAN
  } = options;
  
  // Limit stations for performance
  const effectiveMaxStations = zoomLevel < 8 ? maxStations * 2 : maxStations;
  const processedRecommendations = stationRecommendations.slice(0, effectiveMaxStations);
  
  console.log('🎯 DBSCAN Clustering Input:', {
    stationCount: processedRecommendations.length,
    zoomLevel,
    mapCenter,
    fuelCode,
  });

  // Get DBSCAN parameters for this zoom level from config
  const { epsilon, minPoints } = await clusteringConfigService.getParametersForZoom(zoomLevel);

  // Perform pixel-distance DBSCAN clustering
  const { clusters, noise } = performDBSCANClustering(
    processedRecommendations,
    fuelCode,
    epsilon,
    minPoints,
    zoomLevel,
    mapCenter,
    mapSize
  );

  // Convert noise (individual stations) to the expected format
  const individualStations: FuelStation[] = noise.map(n => n.station);

  const processingTime = performance.now() - startTime;

  console.log(`🎯 DBSCAN Results: ${clusters.length} clusters, ${individualStations.length} individual stations`);
  console.log(`📊 Clusters: ${clusters.map(c => `${c.station_count} stations`).join(', ')}`);
  
  return {
    clusters,
    individualStations,
    debugInfo: {
      totalStations: processedRecommendations.length,
      clusteredStations: clusters.reduce((sum, c) => sum + c.station_count, 0),
      individualStations: individualStations.length,
      processingTime,
    },
  };
}

/**
 * Main visual clustering algorithm (original version for backwards compatibility)
 * Groups stations based on visual overlap in the current viewport
 */
export function performVisualClustering(
  stations: FuelStation[],
  zoomLevel: number,
  mapCenter: Location,
  mapSize: { width: number; height: number },
  options: {
    minClusterSize?: number;
    padding?: number;
    maxStations?: number;
  } = {}
): VisualClusteringResult {
  const startTime = performance.now();
  
  const {
    minClusterSize = 2,
    padding = 8,
    maxStations = 500
  } = options;
  
  // Limit stations for performance, but allow more for low zoom levels
  const effectiveMaxStations = zoomLevel < 8 ? maxStations * 2 : maxStations;
  const processedStations = stations.slice(0, effectiveMaxStations);
  
  // Convert all stations to pixel positions with bounding boxes
  const stationsWithPixels: StationWithPixelPosition[] = processedStations.map(station => {
    const pixelPosition = latLngToPixel(
      station.location.latitude,
      station.location.longitude,
      zoomLevel,
      mapCenter,
      mapSize
    );
    
    const markerSize = getMarkerSize(zoomLevel, false);
    const markerBounds = calculateMarkerBounds(pixelPosition, markerSize, padding);
    
    return {
      ...station,
      pixelPosition,
      markerBounds,
    };
  });
  
  // Track processed stations to avoid duplicate clustering
  const processed = new Set<number>();
  const clusters: VisualClusterData[] = [];
  const individualStations: FuelStation[] = [];
  
  // Process each station for clustering
  for (const station of stationsWithPixels) {
    if (processed.has(station.id)) continue;
    
    // Find all stations that overlap with this one
    const overlappingGroup = findOverlappingStations(station, stationsWithPixels);
    
    if (overlappingGroup.length >= minClusterSize) {
      // Create a cluster from overlapping stations
      const clusterId = `visual-cluster-${clusters.length}`;
      const cluster = createVisualCluster(overlappingGroup, clusterId);
      clusters.push(cluster);
      
      // Mark all stations in this cluster as processed
      overlappingGroup.forEach(s => processed.add(s.id));
    } else {
      // Keep as individual station
      individualStations.push(station);
      processed.add(station.id);
    }
  }
  
  // Merge overlapping clusters to prevent cluster-to-cluster overlap
  const mergedClusters = mergeOverlappingClusters(clusters, stationsWithPixels, zoomLevel, padding);
  
  const processingTime = performance.now() - startTime;
  
  console.log(`🔗 Cluster merging: ${clusters.length} initial clusters → ${mergedClusters.length} final clusters`);
  
  return {
    clusters: mergedClusters,
    individualStations,
    debugInfo: {
      totalStations: processedStations.length,
      clusteredStations: mergedClusters.reduce((sum, c) => sum + c.station_count, 0),
      individualStations: individualStations.length,
      processingTime,
    },
  };
}

/**
 * Calculate color based on price (green for cheap, red for expensive)
 */
export function getPriceBasedColor(
  pricePence: number,
  minPriceGlobal: number = 130, // ~£1.30 typical minimum
  maxPriceGlobal: number = 170  // ~£1.70 typical maximum
): string {
  // Normalize price to 0-1 range
  const normalizedPrice = Math.max(0, Math.min(1, (pricePence - minPriceGlobal) / (maxPriceGlobal - minPriceGlobal)));
  
  // Interpolate between green (cheap) and red (expensive)
  // Green: hsl(120, 70%, 45%) - #4CAF50
  // Yellow: hsl(60, 70%, 45%) - #B8B800  
  // Red: hsl(0, 70%, 45%) - #B83B3B
  
  let hue: number;
  if (normalizedPrice <= 0.5) {
    // Green to yellow (120 to 60 degrees)
    hue = 120 - (normalizedPrice * 2) * 60;
  } else {
    // Yellow to red (60 to 0 degrees)  
    hue = 60 - ((normalizedPrice - 0.5) * 2) * 60;
  }
  
  return `hsl(${hue}, 70%, 45%)`;
}

/**
 * Get cluster marker color based on average price
 */
export function getClusterColor(cluster: VisualClusterData, priceRange?: { min: number; max: number }): string {
  if (!cluster.average_price_pence) {
    return '#6b7280'; // Gray for clusters without pricing data
  }
  
  // If no price range (single cluster), default to green
  if (!priceRange || priceRange.min === priceRange.max) {
    return '#059669'; // Green for single clusters or equal price ranges
  }
  
  const minPrice = priceRange.min;
  const maxPrice = priceRange.max;
  
  return getPriceBasedColor(cluster.average_price_pence, minPrice, maxPrice);
}

/**
 * DBSCAN clustering algorithm for geographic fuel station clustering
 * Groups stations based on geographic proximity rather than visual marker overlap
 */
export function performDBSCANClustering(
  stationsWithPrices: StationRecommendation[],
  fuelCode: string,
  epsilon: number,      // Distance threshold in pixels
  minPoints: number,    // Minimum stations per cluster
  zoomLevel: number,    // For pixel conversion
  mapCenter: Location,  // For pixel conversion
  mapSize: { width: number; height: number } // For pixel conversion
): { clusters: VisualClusterData[], noise: StationRecommendation[] } {
  const stations = stationsWithPrices.map(sp => sp.station);
  const clusters: VisualClusterData[] = [];
  const visited = new Set<number>();
  const clustered = new Set<number>();
  const noise: StationRecommendation[] = [];
  
  // Helper: Calculate pixel distance between two stations
  function getPixelDistance(station1: FuelStation, station2: FuelStation): number {
    const pos1 = latLngToPixel(
      station1.location.latitude, 
      station1.location.longitude, 
      zoomLevel, 
      mapCenter, 
      mapSize
    );
    const pos2 = latLngToPixel(
      station2.location.latitude, 
      station2.location.longitude, 
      zoomLevel, 
      mapCenter, 
      mapSize
    );
    
    const dx = pos1.x - pos2.x;
    const dy = pos1.y - pos2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  // Helper: Find all stations within epsilon pixel distance
  function getNeighbors(stationIndex: number): number[] {
    const neighbors: number[] = [];
    const currentStation = stations[stationIndex];
    
    for (let i = 0; i < stations.length; i++) {
      if (i !== stationIndex) {
        const distance = getPixelDistance(currentStation, stations[i]);
        if (distance <= epsilon) {
          neighbors.push(i);
        }
      }
    }
    
    return neighbors;
  }
  
  // Helper: Create cluster from stations with pricing data
  function createDBSCANCluster(stationIndices: number[], clusterId: string): VisualClusterData {
    const clusterStations = stationIndices.map(i => stations[i]);
    const clusterStationsWithPrices = stationIndices.map(i => stationsWithPrices[i]);
    
    // Calculate cluster center (centroid)
    const avgLat = clusterStations.reduce((sum, s) => sum + s.location.latitude, 0) / clusterStations.length;
    const avgLng = clusterStations.reduce((sum, s) => sum + s.location.longitude, 0) / clusterStations.length;
    
    // Extract brand information
    const brands = [...new Set(clusterStations.map(s => s.brand))];
    const brandCounts = brands.map(brand => ({
      brand,
      count: clusterStations.filter(s => s.brand === brand).length
    }));
    const dominantBrands = brandCounts
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map(b => b.brand);
    
    // Calculate pricing information
    const stationsWithPriceData = clusterStationsWithPrices.filter(sp => {
      const relevantPrice = sp.prices?.find(p => p.fuel_code === fuelCode);
      return relevantPrice?.price_pence || sp.bestPrice?.price_pence;
    });
    
    let average_price_pence: number | undefined;
    let price_range: { min_pence: number; max_pence: number } | undefined;
    
    if (stationsWithPriceData.length > 0) {
      const prices = stationsWithPriceData.map(sp => {
        const relevantPrice = sp.prices?.find(p => p.fuel_code === fuelCode);
        return relevantPrice?.price_pence || sp.bestPrice?.price_pence;
      }).filter(p => p !== undefined) as number[];
      
      if (prices.length > 0) {
        average_price_pence = Math.round(prices.reduce((sum, price) => sum + price, 0) / prices.length);
        price_range = {
          min_pence: Math.min(...prices),
          max_pence: Math.max(...prices),
        };
      }
    }
    
    return {
      id: clusterId,
      stations: clusterStations,
      center: { latitude: avgLat, longitude: avgLng },
      bounds: {
        x: 0, // DBSCAN doesn't use pixel bounds, using dummy values
        y: 0,
        width: 50, // Default cluster size
        height: 50,
      },
      station_count: clusterStations.length,
      brand_count: brands.length,
      brands,
      dominant_brands: dominantBrands,
      average_price_pence,
      price_range,
      stations_with_prices: stationsWithPriceData.length,
    };
  }
  
  // Main DBSCAN algorithm
  for (let i = 0; i < stations.length; i++) {
    if (visited.has(i)) continue;
    
    visited.add(i);
    const neighbors = getNeighbors(i);
    
    if (neighbors.length < minPoints) {
      // Mark as noise (will be individual station)
      continue;
    }
    
    // Create new cluster
    const clusterStations: number[] = [i];
    clustered.add(i);
    
    // Expand cluster
    let j = 0;
    while (j < neighbors.length) {
      const neighborIndex = neighbors[j];
      
      if (!visited.has(neighborIndex)) {
        visited.add(neighborIndex);
        const neighborNeighbors = getNeighbors(neighborIndex);
        
        if (neighborNeighbors.length >= minPoints) {
          // Add new neighbors to expansion list
          neighborNeighbors.forEach(nn => {
            if (!neighbors.includes(nn)) {
              neighbors.push(nn);
            }
          });
        }
      }
      
      if (!clustered.has(neighborIndex)) {
        clusterStations.push(neighborIndex);
        clustered.add(neighborIndex);
      }
      
      j++;
    }
    
    // Create cluster
    const clusterId = `dbscan-cluster-${clusters.length}`;
    const cluster = createDBSCANCluster(clusterStations, clusterId);
    clusters.push(cluster);
  }
  
  // Collect noise points (individual stations)
  for (let i = 0; i < stationsWithPrices.length; i++) {
    if (!clustered.has(i)) {
      noise.push(stationsWithPrices[i]);
    }
  }
  
  return { clusters, noise };
}

/**
 * HDBSCAN (Hierarchical DBSCAN) clustering algorithm.
 *
 * Unlike DBSCAN, HDBSCAN does not require a fixed epsilon. It computes core distances
 * per point (distance to k-th nearest neighbour in pixel space) and builds a minimum
 * spanning tree over mutual reachability distances, then extracts stable clusters from
 * the condensed hierarchy. Sparse stations naturally remain as individual markers;
 * only genuinely dense groups are clustered.
 *
 * Parameters:
 *   minClusterSize — minimum number of stations for a group to be kept as a cluster.
 *   minSamples     — k for the core-distance computation (typically 2).
 */
export function performHDBSCANClustering(
  stationsWithPrices: StationRecommendation[],
  fuelCode: string,
  minClusterSize: number,
  minSamples: number,
  zoomLevel: number,
  mapCenter: Location,
  mapSize: { width: number; height: number }
): { clusters: VisualClusterData[]; noise: StationRecommendation[] } {
  const stations = stationsWithPrices.map(sp => sp.station);
  const n = stations.length;

  if (n === 0) return { clusters: [], noise: [] };
  if (n < minClusterSize) return { clusters: [], noise: stationsWithPrices.slice() };

  // ── Step 1: pixel positions and pairwise distance matrix ──────────────────
  const positions = stations.map(s =>
    latLngToPixel(s.location.latitude, s.location.longitude, zoomLevel, mapCenter, mapSize)
  );

  // Flat n×n symmetric distance matrix
  const dist = new Float64Array(n * n);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = positions[i].x - positions[j].x;
      const dy = positions[i].y - positions[j].y;
      const d = Math.sqrt(dx * dx + dy * dy);
      dist[i * n + j] = d;
      dist[j * n + i] = d;
    }
  }

  // ── Step 2: core distances (k-th nearest neighbour) ───────────────────────
  const k = Math.min(minSamples, n - 1);
  const coreDist = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const row: number[] = [];
    for (let j = 0; j < n; j++) {
      if (j !== i) row.push(dist[i * n + j]);
    }
    row.sort((a, b) => a - b);
    coreDist[i] = row[k - 1] ?? 0;
  }

  // ── Step 3: Prim's MST over mutual reachability distances ─────────────────
  // mrd(i,j) = max(coreDist[i], coreDist[j], dist[i,j])
  const inMST = new Uint8Array(n);
  const minKey = new Float64Array(n).fill(Infinity);
  const parentOf = new Int32Array(n).fill(-1);
  minKey[0] = 0;

  interface MSTEdge { u: number; v: number; weight: number }
  const mstEdges: MSTEdge[] = [];

  for (let iter = 0; iter < n; iter++) {
    // Pick the not-yet-added vertex with the smallest key
    let u = -1;
    for (let v = 0; v < n; v++) {
      if (!inMST[v] && (u === -1 || minKey[v] < minKey[u])) u = v;
    }
    inMST[u] = 1;
    if (parentOf[u] >= 0) {
      mstEdges.push({ u: parentOf[u], v: u, weight: minKey[u] });
    }
    // Relax edges
    for (let v = 0; v < n; v++) {
      if (!inMST[v]) {
        const mrd = Math.max(coreDist[u], coreDist[v], dist[u * n + v]);
        if (mrd < minKey[v]) {
          minKey[v] = mrd;
          parentOf[v] = u;
        }
      }
    }
  }

  // ── Step 4: build dendrogram from MST (sorted by edge weight) ─────────────
  mstEdges.sort((a, b) => a.weight - b.weight);

  // Union-Find: leaf nodes 0..n-1, internal nodes n..2n-2
  const ufParent = Int32Array.from({ length: 2 * n }, (_, i) => i);
  const ufSize = new Int32Array(2 * n);
  for (let i = 0; i < n; i++) ufSize[i] = 1;
  let nextId = n;

  function ufFind(x: number): number {
    while (ufParent[x] !== x) {
      ufParent[x] = ufParent[ufParent[x]];
      x = ufParent[x];
    }
    return x;
  }

  interface DendroMerge {
    nodeId: number;  // new internal node representing the merged cluster
    left: number;
    right: number;
    lambda: number;  // 1 / edge weight (higher = denser)
    leftSize: number;
    rightSize: number;
  }
  const dendrogram: DendroMerge[] = [];

  for (const edge of mstEdges) {
    const rootU = ufFind(edge.u);
    const rootV = ufFind(edge.v);
    if (rootU === rootV) continue;

    const lambda = edge.weight > 0 ? 1 / edge.weight : Infinity;
    const sizeU = ufSize[rootU];
    const sizeV = ufSize[rootV];
    const nodeId = nextId++;

    ufParent[rootU] = nodeId;
    ufParent[rootV] = nodeId;
    ufSize[nodeId] = sizeU + sizeV;

    dendrogram.push({ nodeId, left: rootU, right: rootV, lambda, leftSize: sizeU, rightSize: sizeV });
  }

  // ── Step 5 & 6: condensed cluster tree + per-point lambda_death tracking ──
  //
  // We track for each cluster node:
  //   - lambdaBirth: density at which this cluster was born
  //   - points: leaf indices belonging to this cluster
  //   - stability: Σ (lambda_death_p - lambdaBirth) for each point p
  //
  // "Falling off" happens when a merge produces a component < minClusterSize;
  // those points are released as noise at the current lambda.

  interface ClusterInfo {
    lambdaBirth: number;
    points: Set<number>;
    stability: number;
    childClusterIds: number[];  // internal node IDs of genuine child clusters
  }

  const clusterMap = new Map<number, ClusterInfo>();

  // Collect all leaf points under a (potentially internal) node
  // We build this bottom-up by iterating dendrogram in order
  const nodePoints = new Map<number, number[]>();
  for (let i = 0; i < n; i++) nodePoints.set(i, [i]);

  // Process dendrogram merges bottom-up
  for (const merge of dendrogram) {
    const leftPts = nodePoints.get(merge.left) ?? [];
    const rightPts = nodePoints.get(merge.right) ?? [];
    const allPts = [...leftPts, ...rightPts];
    nodePoints.set(merge.nodeId, allPts);

    const leftIsCluster = clusterMap.has(merge.left);
    const rightIsCluster = clusterMap.has(merge.right);

    if (merge.leftSize >= minClusterSize && merge.rightSize >= minClusterSize) {
      // Both sides are large enough → genuine split: create parent cluster containing both
      const parentCluster: ClusterInfo = {
        lambdaBirth: merge.lambda,
        points: new Set(allPts),
        stability: 0,
        childClusterIds: [],
      };

      // Register child clusters (either already-existing cluster nodes or new leaf-clusters)
      if (!leftIsCluster) {
        // Left side is a dense group but not yet registered → create a cluster for it now
        clusterMap.set(merge.left, {
          lambdaBirth: merge.lambda,
          points: new Set(leftPts),
          stability: 0,
          childClusterIds: [],
        });
      }
      if (!rightIsCluster) {
        clusterMap.set(merge.right, {
          lambdaBirth: merge.lambda,
          points: new Set(rightPts),
          stability: 0,
          childClusterIds: [],
        });
      }
      parentCluster.childClusterIds = [merge.left, merge.right];
      clusterMap.set(merge.nodeId, parentCluster);
    } else {
      // One or both sides are too small → points "fall off" as noise at this lambda
      // The surviving side (if >= minClusterSize) carries on as the current cluster
      const survivingPts = merge.leftSize >= minClusterSize ? leftPts : rightPts;
      const fallingPts = merge.leftSize < minClusterSize ? leftPts : rightPts;

      if (survivingPts.length >= minClusterSize) {
        // Find the existing cluster to inherit from
        const survivingNode = merge.leftSize >= minClusterSize ? merge.left : merge.right;
        let existingCluster = clusterMap.get(survivingNode);
        if (!existingCluster) {
          existingCluster = {
            lambdaBirth: merge.lambda,
            points: new Set(survivingPts),
            stability: 0,
            childClusterIds: [],
          };
          clusterMap.set(survivingNode, existingCluster);
        }
        // Accumulate stability for falling points (they "die" at this lambda)
        for (let _pi = 0; _pi < fallingPts.length; _pi++) {
          existingCluster.stability += merge.lambda - existingCluster.lambdaBirth;
        }
        // Carry this cluster forward under the new internal node
        clusterMap.set(merge.nodeId, existingCluster);
      }
      // else: both sides too small → no cluster formed; all points remain noise
    }
  }

  // Compute final stability for points still in clusters (they "die" when cluster ends)
  for (const [, cluster] of clusterMap) {
    // Each surviving point contributes (lambdaBirth contribution was already partly counted
    // via falling-off accumulation; add remaining contribution for points still inside)
    // Re-derive stability properly: iterate all points in the cluster
    // (This is a simplified stability: sum of 1/λ_birth over all points that joined)
    // For correctness we just use: stability = points.size * (mean_density_at_birth)
    // The greedy selection below still works correctly with this relative measure.
    if (cluster.stability === 0 && cluster.points.size > 0) {
      cluster.stability = cluster.points.size * cluster.lambdaBirth;
    }
  }

  // ── Step 7: greedy cluster selection (bottom-up stability maximisation) ───
  // For each cluster: keep it as a cluster if its stability ≥ sum of child stabilities.
  // Otherwise, propagate children.

  // Find root cluster (node with the most points)
  let rootNode = -1;
  let rootSize = 0;
  for (const [nodeId, cluster] of clusterMap) {
    if (cluster.points.size > rootSize) {
      rootSize = cluster.points.size;
      rootNode = nodeId;
    }
  }

  const selectedClusterNodes = new Set<number>();

  function selectClusters(nodeId: number): number {
    const cluster = clusterMap.get(nodeId);
    if (!cluster) return 0;

    const childTotal = cluster.childClusterIds.reduce(
      (sum, childId) => sum + selectClusters(childId),
      0
    );

    if (cluster.stability >= childTotal && cluster.points.size >= minClusterSize) {
      // Keep this cluster, discard its children
      selectedClusterNodes.add(nodeId);
      for (const childId of cluster.childClusterIds) {
        selectedClusterNodes.delete(childId);
      }
      return cluster.stability;
    }
    return childTotal;
  }

  if (rootNode >= 0) selectClusters(rootNode);

  // ── Step 8: collect clustered points and noise ────────────────────────────
  const clusteredIndices = new Set<number>();
  const resultClusters: VisualClusterData[] = [];

  let clusterCounter = 0;
  for (const nodeId of selectedClusterNodes) {
    const cluster = clusterMap.get(nodeId)!;
    const indices = Array.from(cluster.points);
    if (indices.length < minClusterSize) continue;

    for (const idx of indices) clusteredIndices.add(idx);

    const clusterId = `hdbscan-cluster-${clusterCounter++}`;
    resultClusters.push(buildClusterData(indices, stationsWithPrices, stations, fuelCode, clusterId));
  }

  const noise = stationsWithPrices.filter((_, i) => !clusteredIndices.has(i));

  return { clusters: resultClusters, noise };
}

/**
 * Build a VisualClusterData object from a set of station indices.
 * Extracted as a standalone helper so both DBSCAN and HDBSCAN can reuse it.
 */
function buildClusterData(
  stationIndices: number[],
  stationsWithPrices: StationRecommendation[],
  stations: FuelStation[],
  fuelCode: string,
  clusterId: string
): VisualClusterData {
  const clusterStations = stationIndices.map(i => stations[i]);
  const clusterStationsWithPrices = stationIndices.map(i => stationsWithPrices[i]);

  const avgLat = clusterStations.reduce((s, st) => s + st.location.latitude, 0) / clusterStations.length;
  const avgLng = clusterStations.reduce((s, st) => s + st.location.longitude, 0) / clusterStations.length;

  const brands = [...new Set(clusterStations.map(s => s.brand))];
  const dominantBrands = brands
    .map(brand => ({ brand, count: clusterStations.filter(s => s.brand === brand).length }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map(b => b.brand);

  const priceValues = clusterStationsWithPrices
    .map(sp => sp.prices?.find(p => p.fuel_code === fuelCode)?.price_pence ?? sp.bestPrice?.price_pence)
    .filter((p): p is number => p !== undefined);

  const average_price_pence = priceValues.length
    ? Math.round(priceValues.reduce((s, p) => s + p, 0) / priceValues.length)
    : undefined;
  const price_range = priceValues.length
    ? { min_pence: Math.min(...priceValues), max_pence: Math.max(...priceValues) }
    : undefined;

  return {
    id: clusterId,
    stations: clusterStations,
    center: { latitude: avgLat, longitude: avgLng },
    bounds: { x: 0, y: 0, width: 50, height: 50 },
    station_count: clusterStations.length,
    brand_count: brands.length,
    brands,
    dominant_brands: dominantBrands,
    average_price_pence,
    price_range,
    stations_with_prices: priceValues.length,
  };
}

/**
 * Legacy function - replaced by configurable parameters
 * @deprecated Use clusteringConfigService.getParametersForZoom() instead
 */
export function calculateSimpleDBSCANParameters(
  stationCount: number,
  zoomLevel: number
): { epsilon: number; minPoints: number } {
  console.warn('⚠️ calculateSimpleDBSCANParameters is deprecated. Use clusteringConfigService.getParametersForZoom() instead.');
  
  // Fallback to basic values if called directly
  let epsilon: number;
  if (zoomLevel <= 8) {
    epsilon = 60; // Reduced from 150 to prevent huge clusters
  } else if (zoomLevel <= 11) {
    epsilon = 50;  // Reduced from 80
  } else {
    epsilon = 30;  // Reduced from 40
  }
  
  const minPoints = Math.max(2, Math.min(8, Math.floor(stationCount / 50)));
  
  return { epsilon, minPoints };
}

/**
 * Debug helper to visualize clustering decisions
 */
export function getVisualClusteringDebugInfo(result: VisualClusteringResult): string {
  const { debugInfo, clusters } = result;
  
  return [
    `Visual Clustering Results:`,
    `- Total stations processed: ${debugInfo.totalStations}`,
    `- Clusters created: ${clusters.length} (${debugInfo.clusteredStations} stations)`,
    `- Individual stations: ${debugInfo.individualStations}`,
    `- Processing time: ${debugInfo.processingTime.toFixed(2)}ms`,
    `- Largest cluster: ${Math.max(...clusters.map(c => c.station_count), 0)} stations`,
    `- Average cluster size: ${clusters.length > 0 ? (debugInfo.clusteredStations / clusters.length).toFixed(1) : 0}`,
    `- Clusters with pricing: ${clusters.filter(c => c.average_price_pence).length}`,
    `- Price range: ${Math.min(...clusters.filter(c => c.average_price_pence).map(c => c.average_price_pence!))}p - ${Math.max(...clusters.filter(c => c.average_price_pence).map(c => c.average_price_pence!))}p`,
  ].join('\n');
}